import React, { useEffect, useState } from 'react';
import './style.scoped.scss';
import { BsGearFill } from 'react-icons/bs';
import {
  FaFlask,
  FaRegFileAlt,
  FaKey,
  FaCheck,
  FaArrowRight,
  FaChevronRight,
  FaRegQuestionCircle,
  FaShieldAlt,
  FaLock,
  FaRegCopy,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { ROUTES } from '../../../../assets/constants/strings';
import ApiFlowDiagram from '../../../../assets/images/api-flow-diagram.png';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import clipboardCopy from 'clipboard-copy';
import { canUseApiKeys, displayApiPlan } from '../../../../services/entitlements';
import { getApiUsage, getApiKeys } from '../../../../services/axios';

// Fallback total allowance used only if the API doesn't return a limit. The real cap is the
// caller's total call allowance (getApiUsage.callLimit), a lifetime total (Trial = 100).
const DEFAULT_TOTAL_QUOTA = 100;

// A real, copy-pasteable request using our actual API-key auth scheme (X-Api-Key/X-Api-Secret).
const QUICKSTART_CODE = `GET /api/v1/Search/Get?q=climate%20risk HTTP/1.1
Host: api.regulationcity.com
X-Api-Key: YOUR_API_KEY
X-Api-Secret: YOUR_API_SECRET
Accept: application/json`;

const Overview = ({ user, token }) => {
  const [usage, setUsage] = useState({ calls: 0, limit: 0, planName: null, loading: true });
  const [keyCount, setKeyCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setUsage({ calls: 0, limit: 0, planName: null, loading: false });
        return;
      }
      const now = new Date();
      const [u, keys] = await Promise.all([
        getApiUsage(token, now.getFullYear(), now.getMonth() + 1),
        getApiKeys(token),
      ]);
      if (cancelled) return;
      setUsage({
        calls: (u && u.used) || 0,
        limit: (u && u.callLimit) || 0,
        planName: (u && u.planName) || null,
        loading: false,
      });
      setKeyCount(Array.isArray(keys) ? keys.length : 0);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isProduction = (usage.planName || '').toLowerCase().includes('production');
  const calls = usage.loading ? 0 : usage.calls;
  const limit = usage.limit > 0 ? usage.limit : DEFAULT_TOTAL_QUOTA;
  const hasCap = !usage.loading && usage.limit > 0;
  const remaining = Math.max(0, limit - calls);
  const quotaPct = Math.min(100, Math.round((calls / limit) * 100));

  // Getting-started checklist — completion is derived from real signals.
  const steps = [
    {
      label: 'Review the documentation',
      desc: 'Understand the endpoints, parameters and response format.',
      // No real "read" signal — treat any engagement (a call or a key) as past this step.
      done: calls > 0 || keyCount > 0,
      link: ROUTES.APIDocs,
    },
    {
      label: 'Test a request in API Explorer',
      desc: 'Try live endpoints in the browser — no key required.',
      done: calls > 0,
      link: ROUTES.APIExplorer,
    },
    {
      label: 'Create an API key and test in your environment',
      desc: 'Generate a key and call the API from your own app.',
      done: keyCount > 0,
      link: ROUTES.APIKeys,
    },
    {
      label: 'Request Enterprise access',
      desc: 'Move up from the trial allowance to the Enterprise plan.',
      done: isProduction,
      link: ROUTES.APIPlans,
    },
  ];
  const completed = steps.filter((s) => s.done).length;
  const nextStep = steps.find((s) => !s.done);
  const codeLines = QUICKSTART_CODE.split('\n');

  const copyExample = () => {
    clipboardCopy(QUICKSTART_CODE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className='ov'>
      {/* Hero */}
      <header className='ov-hero'>
        <div className='ov-hero__text'>
          <span className='ov-hero__eyebrow'>Developer Platform</span>
          <h1 className='ov-hero__title'>RegulationCity API</h1>
          <p className='ov-hero__subtitle'>Build, test and manage your API access.</p>
        </div>
        <div className='ov-hero__actions'>
          <Link to={ROUTES.APIExplorer} className='ov-btn ov-btn--primary'>
            <FaFlask /> Open API Explorer
          </Link>
          <Link to={ROUTES.APIDocs} className='ov-btn ov-btn--ghost'>
            <FaRegFileAlt /> View documentation
          </Link>
          {canUseApiKeys(user) && (
            <Link to={ROUTES.APIKeys} className='ov-btn ov-btn--ghost'>
              <FaKey /> Manage API keys
            </Link>
          )}
        </div>
      </header>

      <div className='ov-grid'>
        {/* Left column */}
        <div className='ov-main'>
          {/* Getting started */}
          <section className='ov-card ov-getstarted'>
            <h3 className='ov-card__heading'>Getting started</h3>
            <div className='ov-getstarted__progress'>
              <span className='ov-getstarted__count'>{completed} of {steps.length} completed</span>
              <div className='ov-progress'>
                <div
                  className={`ov-progress__fill${completed === steps.length ? ' is-complete' : ''}`}
                  style={{ width: `${(completed / steps.length) * 100}%` }}
                />
              </div>
            </div>
            <div className='ov-getstarted__body'>
              <ul className='ov-steps'>
                {steps.map((s, i) => (
                  <li key={i} className='ov-step'>
                    <Link to={s.link} className='ov-step__row'>
                      <span className={`ov-step__marker${s.done ? ' is-done' : ''}`}>
                        {s.done ? <FaCheck /> : i + 1}
                      </span>
                      <span className='ov-step__text'>
                        <span className='ov-step__label'>{s.label}</span>
                        <span className='ov-step__desc'>{s.desc}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className='ov-getstarted__aside'>
                <img
                  className='ov-diagram'
                  src={ApiFlowDiagram}
                  alt='RegulationCity API connects regulator content (ASIC, APRA, ACCC and more) to your product'
                />
                {nextStep && (
                  <Link
                    to={nextStep.link}
                    className='ov-btn ov-btn--ghost ov-getstarted__continue'
                  >
                    Continue setup <FaArrowRight />
                  </Link>
                )}
              </div>
            </div>
          </section>

          {/* Start building */}
          <section className='ov-card'>
            <h3 className='ov-card__heading'>Start building</h3>
            <div className='ov-build'>
              <div className='ov-build__card'>
                <span className='ov-build__icon is-doc'>
                  <FaRegFileAlt />
                </span>
                <h4 className='ov-build__title'>Documentation</h4>
                <p className='ov-build__desc'>Explore guides, references and code examples.</p>
                <Link to={ROUTES.APIDocs} className='ov-link'>
                  Open docs <FaArrowRight />
                </Link>
              </div>
              <div className='ov-build__card'>
                <span className='ov-build__icon is-explorer'>
                  <FaFlask />
                </span>
                <h4 className='ov-build__title'>API Explorer</h4>
                <p className='ov-build__desc'>Test endpoints, inspect responses and debug requests.</p>
                <Link to={ROUTES.APIExplorer} className='ov-link'>
                  Start testing <FaArrowRight />
                </Link>
              </div>
              <div className='ov-build__card'>
                <span className='ov-build__icon is-key'>
                  <FaKey />
                </span>
                <h4 className='ov-build__title'>API Keys</h4>
                <p className='ov-build__desc'>Create, view and manage your API keys and permissions.</p>
                <Link
                  to={canUseApiKeys(user) ? ROUTES.APIKeys : ROUTES.APIPlans}
                  className='ov-link'
                >
                  Manage keys <FaArrowRight />
                </Link>
              </div>
            </div>
          </section>

          {/* Quick start */}
          <section className='ov-card ov-quickstart'>
            <div className='ov-quickstart__head'>
              <h3 className='ov-card__heading' style={{ margin: 0 }}>Quick start</h3>
              <div className='ov-quickstart__actions'>
                <Link to={ROUTES.APIExplorer} className='ov-quickstart__action'>
                  <FaExternalLinkAlt /> Try in API Explorer
                </Link>
                <button type='button' className='ov-quickstart__action' onClick={copyExample}>
                  <FaRegCopy /> {copied ? 'Copied' : 'Copy example'}
                </button>
              </div>
            </div>
            <div className='ov-endpoint'>
              <span className='ov-endpoint__method'>GET</span>
              <code className='ov-endpoint__path'>/api/v1/Search/Get</code>
            </div>
            <p className='ov-quickstart__desc'>Search regulations by keyword, jurisdiction or provider.</p>
            <pre className='ov-code'>
              {codeLines.map((ln, i) => (
                <span key={i} className='ov-code__line'>
                  <span className='ov-code__ln'>{i + 1}</span>
                  <span className='ov-code__txt'>{ln}</span>
                </span>
              ))}
            </pre>
          </section>
        </div>

        {/* Right column */}
        <aside className='ov-side'>
          {/* Usage */}
          <section className='ov-card ov-usage'>
            <div className='ov-usage__head'>
              <h3 className='ov-card__heading'>
                {usage.planName ? `${displayApiPlan(usage.planName)} usage` : 'Plan usage'}
              </h3>
              <span className='ov-usage__badge'>
                {displayApiPlan(usage.planName) || (isProduction ? 'Enterprise' : 'Trial')}
              </span>
            </div>
            <p className='ov-usage__note'>Your API calls across all your keys.</p>

            <div className='ov-usage__count'>
              <span className='ov-usage__count__num'>
                {usage.loading ? '—' : calls.toLocaleString()}
              </span>
              {hasCap && (
                <span className='ov-usage__count__total'>/ {limit.toLocaleString()}</span>
              )}
              <span className='ov-usage__count__cap'>API calls used</span>
            </div>

            {hasCap && (
              <>
                <div className='ov-usage__bar'>
                  <div
                    className={`ov-usage__bar__fill${!usage.loading && quotaPct >= 100 ? ' is-complete' : ''}`}
                    style={{ width: `${usage.loading ? 0 : quotaPct}%` }}
                  />
                </div>
                <div className='ov-usage__stats'>
                  <div className='ov-usage__stat'>
                    <span className='ov-usage__stat__icon'>
                      <FaKey />
                    </span>
                    <span className='ov-usage__stat__text'>
                      <span className='ov-usage__stat__val'>{remaining.toLocaleString()}</span>
                      <span className='ov-usage__stat__lbl'>calls remaining</span>
                    </span>
                  </div>
                  <div className='ov-usage__stat'>
                    <span className='ov-usage__stat__icon is-shield'>
                      <FaShieldAlt />
                    </span>
                    <span className='ov-usage__stat__text'>
                      <span className='ov-usage__stat__val'>One-time</span>
                      <span className='ov-usage__stat__lbl'>trial allowance</span>
                    </span>
                  </div>
                </div>
              </>
            )}

            <Link to={ROUTES.APIUsage} className='ov-link'>
              View usage details <FaArrowRight />
            </Link>
          </section>

          {/* Current access */}
          <section className='ov-card ov-access'>
            <h3 className='ov-card__heading'>Current access</h3>
            <Link to={ROUTES.APIKeys} className='ov-access__row'>
              <span className='ov-access__icon is-key'>
                <FaKey />
              </span>
              <span className='ov-access__label'>
                {keyCount} active API key{keyCount === 1 ? '' : 's'}
              </span>
              <FaChevronRight className='ov-access__chev' />
            </Link>
            <Link to={ROUTES.APIUsage} className='ov-access__row'>
              <span className='ov-access__icon is-ok'>
                <FaShieldAlt />
              </span>
              <span className='ov-access__label'>
                {isProduction ? 'Enterprise access enabled' : 'Trial access enabled'}
              </span>
              <FaChevronRight className='ov-access__chev' />
            </Link>
            {!isProduction && (
              <Link to={ROUTES.APIPlans} className='ov-access__row'>
                <span className='ov-access__icon is-locked'>
                  <FaLock />
                </span>
                <span className='ov-access__label'>Enterprise access not enabled</span>
                <FaChevronRight className='ov-access__chev' />
              </Link>
            )}
            {!isProduction && (
              <Link to={ROUTES.APIPlans} className='ov-btn ov-btn--primary ov-access__cta'>
                Request Enterprise access
              </Link>
            )}
          </section>

          {/* Help + Account — grouped rows in one card */}
          <section className='ov-card ov-linkgroup'>
            <Link to={ROUTES.APIDocs} className='ov-linkrow'>
              <span className='ov-linkrow__icon is-help'>
                <FaRegQuestionCircle />
              </span>
              <span className='ov-linkrow__text'>
                <span className='ov-linkrow__head'>Need help?</span>
                <span className='ov-linkrow__sub'>Browse our help centre or contact support.</span>
              </span>
              <FaChevronRight className='ov-linkrow__chev' />
            </Link>
            <Link to={ROUTES.APIAccount} className='ov-linkrow'>
              <span className='ov-linkrow__icon is-acct'>
                <BsGearFill />
              </span>
              <span className='ov-linkrow__text'>
                <span className='ov-linkrow__head'>Account settings</span>
                <span className='ov-linkrow__sub'>Manage your profile, preferences and notifications.</span>
              </span>
              <FaChevronRight className='ov-linkrow__chev' />
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  token: state.user.user ? state.user.user.token : null,
});

export default connect(mapStateToProps, null)(Overview);
